import {
  Component,
  Children,
  cloneElement,
  ReactElement,
  HTMLAttributes,
} from "react";
import { CheckBoxProps, CheckBoxState } from "./CheckBox";
import { InputError } from "../InputError/InputError";
import {
  FormFieldProps,
  FormFieldMultiString,
} from "../../commonTypes/formField.types";
import { hashCode } from "../../utils/hash";
import React from "react";
export interface CheckBoxGroupProps extends FormFieldProps<string[]> {
  fieldNameOverride?: string;
  useChildrensFieldNames?: boolean;
  childrenAttributes?: HTMLAttributes<any>;
}
export interface CheckBoxGroupState extends FormFieldMultiString {
  conditionalState?: boolean;
  childrenFieldNames: string[];
}

const DEFAULT_STATE_VALUE = [];

export class CheckBoxGroup extends Component<
  CheckBoxGroupProps,
  CheckBoxGroupState
> {
  static defaultProps = {
    fieldName: "",
    value: DEFAULT_STATE_VALUE,
    isTouched: false,
    errorMessage: "",
    isRequired: false,
    childrenAttributes: {},
    onChange: () => {
      /* noop */
    },
  };
  private _id: string;
  private _latestValue: string[] | undefined;
  private _errorId: string;

  constructor(props: CheckBoxGroupProps) {
    super(props);
    this._id = props.id || hashCode(props.fieldName, "textInput");
    this._errorId = hashCode(props.id || props.fieldName, "error");

    const { fieldName, value, label, conditionalState, isRequired } = props;

    if (props.value !== undefined) {
      this._latestValue = value;
    } else {
      this._latestValue = DEFAULT_STATE_VALUE;
    }

    const childrenFieldNames = [];
    Children.forEach(
      this.props.children,
      (child: ReactElement<CheckBoxProps>) => {
        childrenFieldNames.push(child.props.fieldName);
      }
    );

    this.state = {
      value: this._latestValue,
      fieldName,
      label,
      isTouched: false,
      errorMessage: "",
      conditionalState,
      isRequired,
      childrenFieldNames,
    };
  }

  private get _errorMessage(): string | undefined {
    let { errorMessage } = this.state;
    if (!errorMessage && this.props.errorMessage) {
      errorMessage = this.props.errorMessage;
    }

    return errorMessage;
  }

  componentWillUnmount() {
    this.clearChildrenFromStore();
  }

  onChangeValue = (checkBoxState: CheckBoxState) => {
    const { value } = this.state;
    const changedValue = checkBoxState.stringValue.toString();
    if (checkBoxState.value) {
      value.push(changedValue);
    } else {
      value.splice(this.evalCheckboxStringValue(value, changedValue), 1);
    }
    this.setState(
      {
        value: value.slice(),
        isTouched: true,
      },
      () => {
        this.props.onChange(this.state);
      }
    );
  };

  evalCheckboxStringValue(value: any[], stringValue: string | number): number {
    return value.findIndex((v) => v.toString() === stringValue.toString());
  }

  clearChildrenFromStore() {
    let emptyState: CheckBoxGroupState = {
      fieldName: this.state.fieldName,
      value: [],
      childrenFieldNames: [],
    };
    this.props.onChange(emptyState);
  }

  private get _ariaAttributes(): HTMLAttributes<any> {
    const { errorAriaAttributes } = this.props;
    if (errorAriaAttributes) return errorAriaAttributes;
    if (this._errorMessage)
      return { "aria-invalid": true, "aria-describedby": this._errorId };
    return null;
  }

  public render() {
    const { children, fieldName, fieldNameOverride, useChildrensFieldNames } =
      this.props;
    const errorMessage = this._errorMessage;
    const { value } = this.state;
    const fieldNameForId = fieldNameOverride ? fieldNameOverride : fieldName;

    const childrenWithProps = Children.map(
      children,
      (child: ReactElement<CheckBoxProps>, index) => {
        const childProps: CheckBoxProps = {
          id: `${fieldNameForId}_${index}`,
          onChangeCallback: this.onChangeValue,
          value:
            this.evalCheckboxStringValue(value, child.props.stringValue) > -1,
          fieldName: useChildrensFieldNames ? child.props.fieldName : fieldName,
          errorAriaAttributes: this._ariaAttributes,
          attributes: this.props.childrenAttributes,
        };
        return cloneElement(child, childProps);
      }
    );
    return (
      <div {...this.props.attributes}>
        {childrenWithProps}
        {errorMessage && (
          <InputError id={this._errorId} errorText={errorMessage} />
        )}
      </div>
    );
  }
}
